import type { ISchemaWithText } from "~/models/IUserEntry";

type TinymceEditorRef = {
  getEditor: () => TinymceEditorEl;
};

type TinymceEditorEl = {
  getContent: () => string;
  focus: () => void;
  contentAreaContainer: HTMLElement;
  undoManager: {
    clear: () => void;
  };
};

export function useSchemaEditor(
  schema: MaybeRef<ISchemaWithText>,
  editMode: MaybeRef<boolean>
) {
  const editorRef = ref<TinymceEditorRef>();
  const editorContent = ref(unref(schema).text);
  const editorInitialized = ref(false);

  const onInit = () => {
    editorInitialized.value = true;
  };

  /** Needed to capture the tocchanged event */
  const editorParent = ref<HTMLElement>();

  const { user } = storeToRefs(useUserStore());
  const lastSavedEntryStore = useLastSavedEntryStore();
  const { lastSavedEntry } = storeToRefs(lastSavedEntryStore);

  const editorDisabled = computed(
    () => !toValue(schema).editable || !unref(editMode)
  );

  const curatedAndAdmin = computed(() =>
    Boolean(unref(schema).curated && user.value?.content_admin)
  );

  //only downloadable for premium users and the content admin, or if it's allowed in a channel that it's coming from
  const isDownloadable = computed(() => {
    const schemaUnwrapped = unref(schema);

    // let it download if it's the content admin
    if (user.value && user.value.content_admin) {
      return true;
    }

    // let it download if the user is the owner
    if (schemaUnwrapped?.is_owned) {
      return true;
    }

    // let it download if it's allowed in a channel
    if (
      (schemaUnwrapped?.channels && schemaUnwrapped?.channels.length > 0) ||
      (schemaUnwrapped?.from_channels &&
        schemaUnwrapped?.from_channels.length > 0)
    ) {
      return useArraySome(
        [
          ...(schemaUnwrapped?.channels ?? []),
          ...(schemaUnwrapped?.from_channels ?? []),
        ],
        (item) => item.download_allowed === true
      ).value;
    }

    // let it download if it's none of the above, and is not curated
    return Boolean(user.value && !schemaUnwrapped.curated);
  });

  const isCurated = computed(() => Boolean(unref(schema).curated));
  const isOwned = computed(() => Boolean(unref(schema).is_owned));

  const getEditor = () => {
    if (!editorRef.value) {
      throw Error(
        "Editor ref is not bound! Have you attached it to the editor element?"
      );
    }
    return editorRef.value.getEditor();
  };

  /**
   * sync content when new entry is loaded
   */
  watch(
    () => unref(schema).id,
    () => {
      editorContent.value = unref(schema).text;
      getEditor().undoManager.clear();
      getEditor().contentAreaContainer.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  );

  /**
   * Focus editor when this schema has just been created.
   * Also reset lastSavedEntry to prevent the editor from being focused again.
   */
  whenever(
    () =>
      editorInitialized.value &&
      !editorDisabled.value &&
      lastSavedEntry.value?.type === "schema" &&
      lastSavedEntry.value?.id === unref(schema).id,
    () => {
      lastSavedEntryStore.setLastSavedEntry(undefined);
      getEditor().focus();
    }
  );

  /**
   * Sync content when editor is changed as the tocchanged event does not do this on its own
   */
  useEventListener(editorParent, "tocchanged", () => {
    editorContent.value = getEditor().getContent();
  });

  return {
    isOwned,
    editorContent,
    editorDisabled,
    curatedAndAdmin,
    editorRef,
    editorParent,
    isDownloadable,
    isCurated,
    onInit,
  };
}
